import * as React from 'react';
import * as styles from './Viewer.css';
import { Helper } from '../../Helper';
import { Environment } from '../../services/Environment';
import DateTimeService from '../../services/DateTimeService';
import IAAuthors from '../panels/authors/IAAuthors';
import { LocalizationService } from '../../services/LocalizationService';
import { IUser } from '../../interfaces/IUser';
import { IInstance } from '../../interfaces/IInstance';
import { IAAttachments } from '../attachments/Attachments';
import { IAFileUploaderFile } from '../fileUploader/FileUploader';
import { IComposerComponent } from '../../interfaces/IComposerTool';
import { Rotator } from '../composer/gallery/rotator/Rotator';
import { Tiles } from '../composer/gallery/tiles/Tiles';
import { Collage } from '../composer/gallery/collage/Collage';
import { IComposerImage } from '../../interfaces/IComposerImage';
import TranslationService from '../../services/TranslationService';
import { IAMessageVideo } from '../messages/video/MessageVideo';
import { IAReader } from '../reader/Reader';
import { ComposerHelper } from '../composer/helper/ComposerHelper';
import { IMessageIAMessageBgImagesFromBlob, FieldToHide } from '../../interfaces/IMessage';
import { IAMessageCategories } from './categories/MessageCategories';
import { IEnvironment } from '../../interfaces/IEnvironment';
import { IAButton } from '../button/Button';
import { IADivider } from '../divider/Divider';
import { IFontSlots } from '../../interfaces/IFontSlots';

export interface IProps {
  token: string;
  tenant: string;
  environment: IEnvironment;
  isMobile: boolean;
  instance: IInstance;
  highlightColor: string;
  component: "Messages" | "Event" | "ContentCreator" | "ContentOrganizer";
  isAdmin?: boolean;
  isPreview?: boolean;
  hideFullViewCoverOverlay?: boolean;
  hideFullViewDate?: boolean;

  id: string;
  imageUrl?: string;
  imageCropSettings?: any;
  imageModified?: string;
  videoUrl?: string
  title: string;
  teaser: string;
  composerComponents: IComposerComponent[];
  categories?: Array<{ id: string, name: string }>;
  startDate: Date;
  endDate?: Date;
  allDayEvent?: boolean;
  location?: string;
  modified: Date;
  author?: IUser;
  contacts: IUser[];
  editor?: IUser;
  attachments?: IAFileUploaderFile[];
  likesEnabled?: boolean;
  commentsEnabled?: boolean;
  likesOnCommentsEnabled?: boolean;
  translateToLanguage?: {
    itemId: string;
    language: string;
  };
  doneTranslating?: () => void;
  onlyShowCoverImageInRollup?: boolean;
  linkEditorImageTarget?: string;
  isFullPage?: boolean;
  imageViewerImagesToShow: (images: IComposerImage[]) => void;
  imageViewerIndexToShow: (index: number) => void;
  iaMessageBgImagesFromBlob?: IMessageIAMessageBgImagesFromBlob;
  onOpenIAMessage?: (instance: string, messageId: string) => void;
  newsLink?: string;
  openNewsLinkInNewTab?: boolean;
  padding: string;
  fontSlots?: IFontSlots;
  showAllButtonLink?: string;
  fieldsToHide?: FieldToHide[];
  isPrinting?: boolean;
}

export interface IState {
  composerComponents: IComposerComponent[];
  originalComposerComponents: { [id: string]: IComposerComponent[] };
  titlesTranslated: { [id: string]: string };
  lastTranslatedTitle: string;
  teasersTranslated: { [id: string]: string };
  gifImageError: boolean;
}

export class IAViewer extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();
  private readonly dateTimeService: DateTimeService = new DateTimeService();
  private readonly translationService: TranslationService = new TranslationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      composerComponents: props.composerComponents,
      originalComposerComponents: {},
      titlesTranslated: {},
      lastTranslatedTitle: undefined,
      teasersTranslated: {},
      gifImageError: false,
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentWillReceiveProps(props: IProps): void {
    if (!this.state.originalComposerComponents[props.id]) {
      this.setState({
        originalComposerComponents: { ...this.state.originalComposerComponents, [props.id]: JSON.parse(JSON.stringify(props.composerComponents)) }
      })
    }
    if (props.translateToLanguage !== this.props.translateToLanguage) {
      if (props.translateToLanguage.language !== undefined && props.translateToLanguage.language !== "") {
        this.getTranslation(props.translateToLanguage.language);
      } else if (props.translateToLanguage.language === "") {
        this.setState({
          composerComponents: this.state.originalComposerComponents[props.id],
          titlesTranslated: { ...this.state.titlesTranslated, [props.id]: undefined },
          teasersTranslated: { ...this.state.teasersTranslated, [props.id]: undefined },
          lastTranslatedTitle: this.props.id
        }, () => this.props.doneTranslating());
      }
    }
    if (props.composerComponents !== this.props.composerComponents) {
      this.setState({ composerComponents: props.composerComponents });
    }
  }

  private convert(): JSX.Element[] {
    let contentElements: JSX.Element[] = [];

    if (this.props?.fieldsToHide && this.props?.fieldsToHide.includes("content")) {

      return [(<></>)];
    }

    this.state.composerComponents?.forEach((component: IComposerComponent) => {
      switch (component.type) {
        case "text":
          if (component.text !== undefined && component.text !== "") {
            const hasBoxStyle = component.style?.padding !== "0px";
            const style = hasBoxStyle ? component.style : {};
            contentElements.push(
              <IAReader
                content={component.text}
                environment={this.props.environment}
                token={this.props.token}
                tenant={this.props.tenant}
                style={{
                  marginBottom: hasBoxStyle ? 10 : undefined,
                  ...style
                }}
                color={"#333333"}
                tableBorderColor='lightgray'
                showTableBorder
                fontSlots={this.props.fontSlots}
                highlightColor={this.props.highlightColor}
                removeMarginBottomFromLastParagraph={hasBoxStyle}
                onOpenIAMessage={(instance, messageId) => this.props.onOpenIAMessage(instance, messageId)}
                onImageClicked={imageUrl => {
                  const image: IComposerImage = {
                    url: imageUrl
                  }
                  this.props.imageViewerIndexToShow(0);
                  this.props.imageViewerImagesToShow([image]);
                }}
              />
            );
          }
          break;
        case "table":
          if (component.table !== undefined && component.table !== "") {
            contentElements.push(<div className={styles.IA_table} dangerouslySetInnerHTML={{ __html: component.table }} />);
          }
          break;
        case "image":
          const width = component.image.imageOrientation === "Custom" ? (this.props.isMobile ? ComposerHelper.getCustomWidth(component.image.width) : component.image.width) : "100%";
          if (component.image && component.image.url !== undefined && component.image.url !== "") {
            let imageUrl = component.image.url ? Helper.getImageUrl(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, component?.image?.url, component?.image?.modified, "Full", component?.image?.cropSettings) : "none";
            if (component.image.imageBlobUrl) {
              imageUrl = component.image.imageBlobUrl;
            }
            if (component.image.url?.endsWith(".gif")) {
              // image api does not support GIFs
              imageUrl = component.image.url;
            }
            contentElements.push(
              <div
                className={styles.IA_imagePreviewContainer}
                onClick={() => {
                  if (component.image.urlLink) {
                    window.open(component.image.urlLink, component.image.openInNewTab ?? "_self");
                  }
                  else {
                    this.props.imageViewerIndexToShow(0);
                    this.props.imageViewerImagesToShow([component.image]);
                  }
                }}
                style={{
                  width: width,
                  height: component.image && component.image.imageOrientation === "Custom"
                    ? "fit-content"
                    : ((component.image.imageOrientation === "Horizontal")
                      ? (this.props.isMobile
                        ? (screen.width - 80) / 16 * 9
                        : 349)
                      : (this.props.isMobile
                        ? (screen.width - 80) / 3 * 4
                        : 867)),
                }}
              >
                {component.image.imageOrientation === "Custom" ?
                  <img
                    src={imageUrl}
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                      borderRadius: component?.image?.borderRadius ?? 0
                    }}
                  />
                  :
                  // There is a bug in html2canvas when converting images that has object-fit: cover
                  // So we need to use DIV for cases where image might be cropped by the frontend
                  // This is needed for the print feature
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      borderRadius: component?.image?.borderRadius ?? 0
                    }}
                  />
                }
              </div>
            );
          }
          break;
        case "gallery":
          if (component.gallery.images && component.gallery.images.length > 0) {
            let gallery;
            switch (component.gallery.type) {
              case "Rotator":
                gallery = (
                  <div
                    style={{
                      position: "relative",
                      float: "left",
                      marginTop: 10,
                      clear: "both",
                      width: "100%",
                    }}
                  >
                    <Rotator
                      images={component.gallery.images}
                      token={this.props.token}
                      tenant={this.props.tenant}
                      environment={this.props.environment}
                      editMode={false}
                      isMobile={this.props.isMobile}
                      openImageViewer={(index: number) => {
                        this.props.imageViewerIndexToShow(index);
                        this.props.imageViewerImagesToShow(component.gallery.images);
                      }}
                      bordeRadius={component.gallery.borderRadius}
                    />
                  </div>
                );
                break;
              case "Tiles":
                gallery = (
                  <div
                    style={{
                      position: "relative",
                      float: "left",
                      marginTop: 10,
                      clear: "both",
                      width: "calc(100% + 2px)",
                      marginLeft: -2
                    }}
                  >
                    <Tiles
                      maxImages={component.gallery.maxImages}
                      images={component.gallery.images}
                      token={this.props.token}
                      tenant={this.props.tenant}
                      environment={this.props.environment}
                      editMode={false}
                      openImageViewer={(index: number) => {
                        this.props.imageViewerIndexToShow(index);
                        this.props.imageViewerImagesToShow(component.gallery.images);
                      }}
                      bordeRadius={component.gallery.borderRadius}
                    />
                  </div>
                );
                break;
              case "Collage":
                gallery = (
                  <div
                    style={{
                      position: "relative",
                      float: "left",
                      marginTop: 10,
                      clear: "both",
                      width: "100%",
                    }}
                  >
                    <Collage
                      maxImages={component.gallery.maxImages}
                      images={component.gallery.images}
                      token={this.props.token}
                      tenant={this.props.tenant}
                      environment={this.props.environment}
                      editMode={false}
                      openImageViewer={(index: number) => {
                        this.props.imageViewerIndexToShow(index);
                        this.props.imageViewerImagesToShow(component.gallery.images);
                      }}
                      bordeRadius={component.gallery.borderRadius}
                    />
                  </div>
                );
                break;
              default:
                break;
            }
            contentElements.push(
              gallery
            );
          }
          break;

        case "button":

          let label = component.button?.text ?? "...";

          contentElements.push(

            <div style={{ width: "100%", display: "flex", justifyContent: component.button?.align }}>

              <IAButton
                label={label}
                textColor={component.button?.textColor}
                buttonColor={component.button?.backgroundColor}
                borderRadius={component.button?.borderRadius}
                fontSize={component.button?.textSize}
                height={component.button?.textSize * 2.5}
                onClick={() =>
                  window.open(component.button?.url, '_blank', 'noreferrer')
                }
                style={{ marginTop: 15 }}
                fontFamily={this.props.fontSlots?.body?.fontFamily}
              >
              </IAButton>

            </div>
          );
          break;


        case "divider":
          contentElements.push(

            <IADivider
              color={component.divider?.color}
              weight={component.divider?.weight}
              length={component.divider?.length}
              style={{ marginTop: 15 }}
            >
            </IADivider>
          );

          break;

        case "video":
          let videoUrl = component.video?.url;
          if ((location.href.includes("sharepoint.com") && component.video?.embedCode) || (videoUrl == undefined && component.video?.embedCode)) {
            videoUrl = Helper.getVideoSrcFromEmbedCode(component.video?.embedCode);
          }
          contentElements.push(
            <IAMessageVideo
              id={this.props.id}
              width={"100%"}
              height={this.props.isMobile ? (screen.width - 80) / 16 * 9 : 349}
              videoUrl={videoUrl}
              environment={this.props.environment}
              tenant={this.props.tenant}
              token={this.props.token}
              style={{
                float: "left",
                margin: "10px 0"
              }}
            />
          );
          break;
        case "audio":
          contentElements.push(
            <audio
              controls
              preload="auto"
              style={{
                width: "100%",
                float: "left",
                margin: "10px 0"
              }}
            >
              <source src={component.audio?.url?.includes("sharepoint.com") ? Helper.getVideoUrl(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, component.audio?.url) : component.audio?.url} type="audio/mp3" />
            </audio>
          );
          break;
        default:
          break;
      }
    });
    return contentElements;
  }

  private getTranslation(language: string): void {
    const content = [];
    if (this.props.composerComponents.length > 0) {
      this.props.composerComponents?.forEach((component: IComposerComponent) => {
        if (component.type === "text" || component.type === "heading") {
          content.push(component.text);
        }
      });
    }
    for (let index = 0; index < content.length; index++) {
      const phrase = content[index];
      this.translationService.getTranslations(this.props.environment, this.props.token, this.props.tenant, this.props.title, this.props.teaser, this.props.categories ? this.props.categories.reduce((x, y) => { return x + (x ? ", " : "") + y.name }, "") : undefined, phrase, language).then((translation) => {
        let title = translation[0];
        let text = translation[2];
        let teaser = translation[3];
        const composerComponents = this.state.composerComponents;
        let textComponentIndex = 0;
        composerComponents?.forEach((component: IComposerComponent) => {
          if (component.type === "text" || component.type === "heading") {
            if (textComponentIndex == index) {
              component.text = text;
            }
            textComponentIndex++;
          }
        });
        this.setState({
          composerComponents,
          titlesTranslated: { ...this.state.titlesTranslated, [this.props.id]: JSON.parse(JSON.stringify(title)) },
          teasersTranslated: { ...this.state.teasersTranslated, [this.props.id]: JSON.parse(JSON.stringify(teaser)) },
          lastTranslatedTitle: this.props.id
        }, () => this.props.doneTranslating());
      });
    }
  }

  public render(): JSX.Element {
    // Date
    let date: string;

    if (this.props.startDate != undefined) {
      date = this.dateTimeService.ConvertToDDMMYYYY(this.props.startDate) === this.dateTimeService.ConvertToDDMMYYYY(new Date()) && !this.props.isPrinting ? new LocalizationService().strings.Messages_ViewPanel_Today + " " + this.dateTimeService.ConvertToHHMMWithDoubleDots(this.props.startDate) : this.dateTimeService.ConvertToDDMMYYYYHHMM(this.props.startDate, true);
    }
    // Author
    let authors = [];

    const hideAuthors = this.props.fieldsToHide && this.props.fieldsToHide?.includes('author');
    if (!hideAuthors) {
      if (this.props.contacts && this.props.contacts.length > 0) {
        authors = this.props.contacts;
      }
      if (authors.length == 0 && this.props.author != undefined) {
        authors.push(this.props.author);
      }
    }

    const coverDesktopHeight = this.props.isFullPage ? 450 : 394;
    const coverImage = this.props.iaMessageBgImagesFromBlob?.StandardLarge ? this.props.iaMessageBgImagesFromBlob.StandardLarge : Helper.getImageUrl(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, this.props.imageUrl, this.props.imageModified, "StandardLarge", this.props.imageCropSettings);
    const thumbnailUrl = this.props.videoUrl && this.props.videoUrl !== "" && !(this.props.imageUrl && this.props.onlyShowCoverImageInRollup) ? coverImage : undefined;
    const hideCover = this.props.fieldsToHide && this.props.fieldsToHide?.includes('cover');
    return (
      <div className={styles.IA_viewer}>

        {!hideCover && ((this.props.imageUrl && !this.props.onlyShowCoverImageInRollup) || this.props.videoUrl) &&

          <div
            className={styles.IA_coverPreviewContainer}
            style={{
              height: this.props.isMobile ? screen.width / 16 * 9 : coverDesktopHeight
            }}
          >
            {!this.props.videoUrl && !this.props.onlyShowCoverImageInRollup &&
              <img
                className={styles.IA_imagePreview}
                src={this.state.gifImageError ? coverImage.replace("StandardLarge", "StandardSmall") : coverImage}
                draggable={false}
                onClick={() => {
                  this.props.imageViewerIndexToShow(0);
                  this.props.imageViewerImagesToShow([{
                    url: this.props.imageUrl,
                  }]);
                }}
                onError={() => {
                  if (coverImage?.includes(".gif") && coverImage?.includes("StandardLarge") && !this.state.gifImageError) {
                    this.setState({ gifImageError: true });
                  }
                }}
              />
            }
            {this.props.videoUrl && (
              <IAMessageVideo
                id={this.props.id}
                width={"100%"}
                height={"100%"}
                videoUrl={this.props.videoUrl}
                environment={this.props.environment}
                tenant={this.props.tenant}
                token={this.props.token}
                thumbnailUrl={thumbnailUrl}
              />
            )}
          </div>
        }
        <div
          className={styles.IA_viewPanelContent}
          style={{ padding: this.props.padding }}
        >
          {this.props.categories != undefined && this.props.categories.length > 0 &&
            <IAMessageCategories
              categories={this.props.categories}
              fontSlots={this.props.fontSlots}
              style={{ marginTop: 15, float: "left" }}
              archiveLink={this.props.showAllButtonLink}
              textColorWhenTransparentBackground="#333333"
            />
          }
          {this.props.title ?
            <div
              className={styles.IA_title}
              style={{
                marginTop: this.props.categories != undefined && this.props.categories.length > 0 ? 0 : 10,
                fontFamily: this.props.fontSlots?.heading?.fontFamily
              }}
            >
              <h1>{this.state.titlesTranslated[this.props.id] ?? this.props.title}</h1>
            </div>
            :
            <div
              className={styles.IA_missingTitle}
              style={{
                marginTop: this.props.categories != undefined && this.props.categories.length > 0 ? 0 : 10,
                fontFamily: this.props.fontSlots?.heading?.fontFamily
              }}
            >
              <h1>{this.localizationService.strings.Viewer_MissingTitle}</h1>
            </div>
          }
          {date && this.props.component === "Messages" &&
            <div
              style={{
                float: "left",
                marginBottom: 15,
                fontSize: 13,
                color: "#858585",
                fontFamily: this.props.fontSlots?.body?.fontFamily
              }}
            >
              {date}
            </div>
          }
          {authors != undefined && this.props.component === "Messages" &&
            <IAAuthors
              highlightColor={this.props.highlightColor}
              authors={authors}
              style={{
                clear: "both",
                fontFamily: this.props.fontSlots?.body?.fontFamily
              }}
            />
          }
          {this.props.component === "Event" &&
            <div
              className={styles.IA_time}
              style={{
                fontFamily: this.props.fontSlots?.body?.fontFamily
              }}
            >
              {(this.props.startDate && this.props.endDate && !this.props.hideFullViewDate) &&
                <div className={styles.IA_propertyValue}><b>{this.localizationService.strings.Events_Date}: </b>{this.dateTimeService.getEventDateString(this.props.startDate, this.props.endDate, this.props.allDayEvent)}</div>
              }
              {(this.props.startDate && this.props.endDate && !this.props.allDayEvent && !this.props.hideFullViewDate) &&
                <div className={styles.IA_propertyValue}><b>{this.localizationService.strings.Events_Time}: </b>{this.dateTimeService.getEventTimeString(this.props.startDate, this.props.endDate)}</div>
              }
              {this.props.location &&
                <div className={styles.IA_propertyValue}><b>{this.localizationService.strings.Events_Location}: </b>{this.props.location}</div>
              }
            </div>
          }
          {this.props.teaser && this.props.instance?.settings?.enableTeaser &&
            <div
              className={styles.IA_teaser}
              style={{
                fontFamily: this.props.fontSlots?.body?.fontFamily
              }}
            >
              {this.state.teasersTranslated[this.props.id] ?? this.props.teaser}
            </div>
          }
          {this.convert()}
          {this.props.isAdmin &&
            <div className={styles.modifiedBy}>{this.localizationService.strings.Messages_ViewPanel_ModifiedBy} {this.props.editor != undefined ? this.props.editor?.displayName : (this.props.author ? this.props.author.displayName : "-")} - {this.dateTimeService.ConvertToDDMMYYYY(this.props.modified) == this.dateTimeService.ConvertToDDMMYYYY(new Date()) && !this.props.isPrinting ? this.localizationService.strings.DateTimeService_Today + " " + this.dateTimeService.ConvertToHHMM(this.props.modified) : this.dateTimeService.ConvertToDDMMYYYYHHMM(this.props.modified)}</div>
          }
          {!!this.props.newsLink &&
            <div className={styles.IA_readMoreNewsLink}>
              <IAButton
                buttonColor={this.props.highlightColor}
                label={this.localizationService.strings.Viewer_NewsLinkReadMore}
                borderRadius={5}
                mouseOverButtonColor={Helper.getColorWithOpacity(this.props.highlightColor, 0.9, this.props.highlightColor)}
                style={{
                  height: "auto",
                  padding: "5px 25px"
                }}
                onClick={() => window.open(this.props.newsLink, this.props.openNewsLinkInNewTab ? "_blank" : "_self")}
              />
            </div>
          }
          {this.props.attachments &&
            <IAAttachments
              target="_blank"
              attachments={this.props.attachments}
              linkColor={this.props.highlightColor}
              style={{
                marginTop: 15,
                marginRight: 40,
                marginBottom: this.props.isMobile && !this.props.likesEnabled && !this.props.commentsEnabled ? 100 : 20,
                marginLeft: 0,
                float: "left",
                width: "100%"
              }}
            />
          }
        </div>
      </div>
    );
  }
}